import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Save, X, LogOut, Filter, Edit2, Check, Layers, Box, Droplets } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Funzione per ottenere data/ora italiana corrente
const getItalianDateTime = () => {
  const now = new Date();
  const italianTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Rome' }));
  const year = italianTime.getFullYear();
  const month = String(italianTime.getMonth() + 1).padStart(2, '0');
  const day = String(italianTime.getDate()).padStart(2, '0');
  const hours = String(italianTime.getHours()).padStart(2, '0');
  const minutes = String(italianTime.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const Bins = () => {
  const [bins, setBins] = useState([]);
  const [filteredBins, setFilteredBins] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [idFornitori, setIdFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showUscitaModal, setShowUscitaModal] = useState(false);
  const [selectedBin, setSelectedBin] = useState(null);
  const [editingBin, setEditingBin] = useState(null);
  const [editForm, setEditForm] = useState({});
  
  // Multi-Bins
  const [isMultiBins, setIsMultiBins] = useState(false);
  const [multiBinsItems, setMultiBinsItems] = useState([{ bin_number: '', kg: '', doccia_number: 1 }]);
  
  // Filtri
  const [filterData, setFilterData] = useState('');
  const [filterDoccia, setFilterDoccia] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [groupBy, setGroupBy] = useState('none');
  const [formData, setFormData] = useState({
    bin_number: '',
    doccia_number: 1,
    lotto_number: '',
    fornitore: '',
    id_fornitore: '',
    prodotto: '',
    kg: '',
    data_inizio: getItalianDateTime()
  });
  const [uscitaData, setUscitaData] = useState({
    kg_uscita: '',
    data_ora_uscita: getItalianDateTime(),
    nuovo_bin_number: '',
    nuova_doccia_number: ''
  });
  const [prodotti, setProdotti] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filterData, filterDoccia, searchTerm, groupBy, bins]);

  const fetchData = async () => {
    try {
      const [binsRes, fornitoriRes, prodottiRes, idFornitoriRes] = await Promise.all([
        axios.get(`${API}/bins`),
        axios.get(`${API}/fornitori`),
        axios.get(`${API}/prodotti`),
        axios.get(`${API}/id-fornitori`)
      ]);
      const binsAttivi = binsRes.data.filter(b => b.kg_attuali > 0);
      setBins(binsAttivi);
      setFornitori(fornitoriRes.data);
      setProdotti(prodottiRes.data);
      setIdFornitori(idFornitoriRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...bins];
    if (filterDoccia !== 'all') {
      filtered = filtered.filter(b => b.doccia_number === parseInt(filterDoccia));
    }
    if (filterData) {
      filtered = filtered.filter(b => {
        const binDate = format(new Date(b.data_inizio), 'yyyy-MM-dd');
        return binDate === filterData;
      });
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(b =>
        b.bin_number.toLowerCase().includes(term) ||
        b.lotto_number.toLowerCase().includes(term) ||
        b.fornitore.toLowerCase().includes(term) ||
        (b.prodotto && b.prodotto.toLowerCase().includes(term))
      );
    }
    
    if (groupBy === 'doccia') {
      filtered.sort((a, b) => a.doccia_number - b.doccia_number);
    } else if (groupBy === 'lotto') {
      filtered.sort((a, b) => a.lotto_number.localeCompare(b.lotto_number));
    }
    
    setFilteredBins(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.id_fornitore) {
      toast.error('Seleziona un ID');
      return;
    }
    try {
      await axios.post(`${API}/bins`, {
        ...formData,
        doccia_number: parseInt(formData.doccia_number),
        kg: parseFloat(formData.kg),
        data_inizio: new Date(formData.data_inizio).toISOString()
      });
      toast.success('Bin creato con successo');
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleMultiBinsSubmit = async (e) => {
    e.preventDefault();
    
    const validBins = multiBinsItems.filter(b => b.bin_number && b.kg);
    if (validBins.length === 0) {
      toast.error('Inserisci almeno un bin valido');
      return;
    }
    if (!formData.lotto_number || !formData.fornitore) {
      toast.error('Lotto e Fornitore sono obbligatori');
      return;
    }
    if (!formData.id_fornitore) {
      toast.error('ID Fornitore è obbligatorio');
      return;
    }

    try {
      const response = await axios.post(`${API}/bins/multi`, {
        lotto_number: formData.lotto_number,
        fornitore: formData.fornitore,
        id_fornitore: formData.id_fornitore,
        prodotto: formData.prodotto || null,
        data_inizio: new Date(formData.data_inizio).toISOString(),
        bins: validBins.map(b => ({
          bin_number: b.bin_number,
          kg: parseFloat(b.kg),
          doccia_number: parseInt(b.doccia_number)
        }))
      });
      
      toast.success(response.data.message);
      if (response.data.errors) {
        response.data.errors.forEach(err => toast.warning(err));
      }
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const addMultiBinItem = () => {
    setMultiBinsItems([...multiBinsItems, { bin_number: '', kg: '', doccia_number: 1 }]);
  };

  const removeMultiBinItem = (index) => {
    if (multiBinsItems.length > 1) {
      setMultiBinsItems(multiBinsItems.filter((_, i) => i !== index));
    }
  };

  const updateMultiBinItem = (index, field, value) => {
    const updated = [...multiBinsItems];
    updated[index][field] = value;
    setMultiBinsItems(updated);
  };

  const getTotalKgMultiBins = () => {
    return multiBinsItems.reduce((sum, item) => sum + (parseFloat(item.kg) || 0), 0).toFixed(2);
  };

  const startEdit = (bin) => {
    setEditingBin(bin.bin_number);
    setEditForm({
      bin_number: bin.bin_number,
      doccia_number: bin.doccia_number,
      lotto_number: bin.lotto_number,
      fornitore: bin.fornitore,
      id_fornitore: bin.id_fornitore || '',
      prodotto: bin.prodotto || '',
      kg_iniziali: bin.kg_iniziali,
      kg_attuali: bin.kg_attuali,
      data_inizio: new Date(bin.data_inizio).toISOString().slice(0, 16)
    });
  };

  const cancelEdit = () => {
    setEditingBin(null);
    setEditForm({});
  };

  const saveEdit = async (originalBinNumber) => {
    try {
      await axios.put(`${API}/bins/${originalBinNumber}`, {
        bin_number: editForm.bin_number,
        doccia_number: parseInt(editForm.doccia_number),
        lotto_number: editForm.lotto_number,
        fornitore: editForm.fornitore,
        id_fornitore: editForm.id_fornitore || null,
        prodotto: editForm.prodotto || null,
        kg_iniziali: parseFloat(editForm.kg_iniziali),
        kg_attuali: parseFloat(editForm.kg_attuali),
        data_inizio: new Date(editForm.data_inizio).toISOString()
      });
      toast.success('Bin aggiornato con successo');
      setEditingBin(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante aggiornamento');
    }
  };

  const handleUscitaParziale = (bin) => {
    setSelectedBin(bin);
    setUscitaData({ 
      kg_uscita: '', 
      data_ora_uscita: getItalianDateTime(),
      nuovo_bin_number: '', 
      nuova_doccia_number: '' 
    });
    setShowUscitaModal(true);
  };

  const handleSubmitUscita = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        bin_number: selectedBin.bin_number,
        kg_uscita: parseFloat(uscitaData.kg_uscita),
        data_ora_uscita: new Date(uscitaData.data_ora_uscita).toISOString()
      };
      if (uscitaData.nuovo_bin_number) {
        payload.nuovo_bin_number = uscitaData.nuovo_bin_number;
      }
      if (uscitaData.nuova_doccia_number) {
        payload.nuova_doccia_number = parseInt(uscitaData.nuova_doccia_number);
      }
      const response = await axios.post(`${API}/bins/uscita-parziale`, payload);
      toast.success(`Uscita registrata! Residuo: ${response.data.kg_residuo} kg`);
      fetchData();
      setShowUscitaModal(false);
      setSelectedBin(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante uscita');
    }
  };

  const handleDelete = async (bin_number) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo bin?')) return;
    try {
      await axios.delete(`${API}/bins/${bin_number}`);
      toast.success('Bin eliminato con successo');
      fetchData();
    } catch (error) {
      toast.error('Errore durante eliminazione');
    }
  };

  const resetForm = () => {
    setFormData({ 
      bin_number: '', 
      doccia_number: 1, 
      lotto_number: '', 
      fornitore: '', 
      id_fornitore: '',
      prodotto: '',
      kg: '',
      data_inizio: getItalianDateTime()
    });
    setIsMultiBins(false);
    setMultiBinsItems([{ bin_number: '', kg: '', doccia_number: 1 }]);
    setShowForm(false);
  };

  // Raggruppa bins per doccia per la preview
  const getBinsByDoccia = () => {
    const byDoccia = {};
    for (let i = 1; i <= 12; i++) {
      byDoccia[i] = [];
    }
    bins.forEach(bin => {
      const doccia = bin.doccia_number || 1;
      if (byDoccia[doccia]) {
        byDoccia[doccia].push(bin);
      }
    });
    // Ordina per posizione in ogni doccia
    Object.keys(byDoccia).forEach(d => {
      byDoccia[d].sort((a, b) => (a.posizione || 1) - (b.posizione || 1));
    });
    return byDoccia;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="loading-indicator">
        <div className="w-8 h-8 border-4 border-[#FFE500] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bins-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-[#FFE500] p-2 rounded-xl">
            <Box className="w-5 h-5 text-[#0A1628]" />
          </div>
          <h2 className="text-2xl font-bold text-white uppercase tracking-tight">Gestione Bins</h2>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold uppercase tracking-wide text-xs transition-colors ${
            showForm 
              ? 'bg-white/10 text-white border border-white/20 hover:bg-white/20' 
              : 'bg-[#FFE500] text-[#0A1628] shadow-lg shadow-[#FFE500]/20 hover:bg-[#FFE500]/90'
          }`}
          data-testid="add-bin-btn"
        >
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? 'Annulla' : 'Nuovo Bin'}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5" data-testid="bin-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white uppercase tracking-tight">
              {isMultiBins ? 'Multi-Bins (Stessa Partita)' : 'Nuovo Bin'}
            </h3>
            <label className="flex items-center gap-2 cursor-pointer bg-white/10 px-3 py-2 rounded-xl border border-white/20">
              <input
                type="checkbox"
                checked={isMultiBins}
                onChange={(e) => setIsMultiBins(e.target.checked)}
                className="w-5 h-5 rounded border-white/20 text-[#FFE500] focus:ring-[#FFE500] bg-white/10"
                data-testid="multi-bins-checkbox"
              />
              <Layers className="w-4 h-4 text-[#FFE500]" />
              <span className="text-sm font-medium text-white">Multi-Bins</span>
            </label>
          </div>

          {!isMultiBins ? (
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Numero Bin</label>
                <input
                  type="text"
                  value={formData.bin_number}
                  onChange={(e) => setFormData({ ...formData, bin_number: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                  required
                  placeholder="es. 19"
                  data-testid="bin-number-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Doccia/Fila</label>
                <select
                  value={formData.doccia_number}
                  onChange={(e) => setFormData({ ...formData, doccia_number: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                  required
                  data-testid="bin-doccia-select"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                    <option key={num} value={num} className="bg-[#0A1628]">Doccia {num}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Lotto</label>
                <input
                  type="text"
                  value={formData.lotto_number}
                  onChange={(e) => setFormData({ ...formData, lotto_number: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                  required
                  placeholder="es. 3096"
                  data-testid="bin-lotto-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Fornitore</label>
                <select
                  value={formData.fornitore}
                  onChange={(e) => setFormData({ ...formData, fornitore: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                  required
                  data-testid="bin-fornitore-select"
                >
                  <option value="" className="bg-[#0A1628]">Seleziona Fornitore</option>
                  {fornitori.map(f => (
                    <option key={f.nome} value={f.nome} className="bg-[#0A1628]">{f.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1 uppercase">ID</label>
                <select
                  value={formData.id_fornitore}
                  onChange={(e) => setFormData({ ...formData, id_fornitore: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-cyan-500/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  required
                  data-testid="bin-id-fornitore-select"
                >
                  <option value="" className="bg-[#0A1628]">Seleziona ID</option>
                  {idFornitori.map(idf => (
                    <option key={idf.codice} value={idf.codice} className="bg-[#0A1628]">
                      {idf.codice}{idf.descrizione ? ` - ${idf.descrizione}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Prodotto</label>
                <select
                  value={formData.prodotto}
                  onChange={(e) => setFormData({ ...formData, prodotto: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                  required
                  data-testid="bin-prodotto-select"
                >
                  <option value="" className="bg-[#0A1628]">Seleziona Prodotto</option>
                  {prodotti.map(p => (
                    <option key={p.nome} value={p.nome} className="bg-[#0A1628]">{p.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1 uppercase">KG</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.kg}
                  onChange={(e) => setFormData({ ...formData, kg: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                  required
                  placeholder="es. 219.00"
                  data-testid="bin-kg-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Data e Ora Ingresso</label>
                <input
                  type="datetime-local"
                  value={formData.data_inizio}
                  onChange={(e) => setFormData({ ...formData, data_inizio: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                  required
                  data-testid="bin-data-input"
                />
              </div>
              <div className="md:col-span-3">
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-[#FFE500] text-[#0A1628] rounded-xl font-bold uppercase tracking-wide text-xs shadow-lg shadow-[#FFE500]/20 hover:bg-[#FFE500]/90 transition-colors"
                  data-testid="save-bin-btn"
                >
                  <Save className="w-4 h-4" />
                  Salva Bin
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleMultiBinsSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                <div>
                  <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Lotto (comune)</label>
                  <input
                    type="text"
                    value={formData.lotto_number}
                    onChange={(e) => setFormData({ ...formData, lotto_number: e.target.value })}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                    required
                    placeholder="es. 3096"
                    data-testid="multi-lotto-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-white/70 mb-1 uppercase">ID</label>
                  <select
                    value={formData.id_fornitore}
                    onChange={(e) => setFormData({ ...formData, id_fornitore: e.target.value })}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    required
                    data-testid="multi-id-fornitore-select"
                  >
                    <option value="" className="bg-[#0A1628]">Seleziona ID</option>
                    {idFornitori.map(idf => (
                      <option key={idf.codice} value={idf.codice} className="bg-[#0A1628]">
                        {idf.codice}{idf.descrizione ? ` - ${idf.descrizione}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Fornitore (comune)</label>
                  <select
                    value={formData.fornitore}
                    onChange={(e) => setFormData({ ...formData, fornitore: e.target.value })}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                    required
                    data-testid="multi-fornitore-select"
                  >
                    <option value="" className="bg-[#0A1628]">Seleziona Fornitore</option>
                    {fornitori.map(f => (
                      <option key={f.nome} value={f.nome} className="bg-[#0A1628]">{f.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Prodotto (comune)</label>
                  <select
                    value={formData.prodotto}
                    onChange={(e) => setFormData({ ...formData, prodotto: e.target.value })}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                    required
                    data-testid="multi-prodotto-select"
                  >
                    <option value="" className="bg-[#0A1628]">Seleziona Prodotto</option>
                    {prodotti.map(p => (
                      <option key={p.nome} value={p.nome} className="bg-[#0A1628]">{p.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Data/Ora Ingresso</label>
                  <input
                    type="datetime-local"
                    value={formData.data_inizio}
                    onChange={(e) => setFormData({ ...formData, data_inizio: e.target.value })}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                    required
                    data-testid="multi-data-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-bold text-white uppercase">Bins da creare</h4>
                  <div className="text-sm text-white/50">
                    Totale: <span className="font-bold text-[#FFE500]">{getTotalKgMultiBins()} kg</span> in <span className="font-bold text-white">{multiBinsItems.length}</span> bins
                  </div>
                </div>
                
                {multiBinsItems.map((item, index) => (
                  <div key={index} className="flex gap-3 items-end p-3 bg-white/5 border border-white/10 rounded-xl">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-white/50 mb-1">N. Bin</label>
                      <input
                        type="text"
                        value={item.bin_number}
                        onChange={(e) => updateMultiBinItem(index, 'bin_number', e.target.value)}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                        placeholder="es. 19"
                        data-testid={`multi-bin-number-${index}`}
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-white/50 mb-1">KG</label>
                      <input
                        type="number"
                        step="0.01"
                        value={item.kg}
                        onChange={(e) => updateMultiBinItem(index, 'kg', e.target.value)}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                        placeholder="es. 150"
                        data-testid={`multi-bin-kg-${index}`}
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-white/50 mb-1">Doccia</label>
                      <select
                        value={item.doccia_number}
                        onChange={(e) => updateMultiBinItem(index, 'doccia_number', e.target.value)}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                        data-testid={`multi-bin-doccia-${index}`}
                      >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                          <option key={num} value={num} className="bg-[#0A1628]">Doccia {num}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeMultiBinItem(index)}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-xl transition-colors"
                      disabled={multiBinsItems.length === 1}
                      data-testid={`remove-multi-bin-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addMultiBinItem}
                  className="w-full py-2 border-2 border-dashed border-white/20 text-white/50 hover:border-[#FFE500] hover:text-[#FFE500] rounded-xl transition-colors flex items-center justify-center gap-2"
                  data-testid="add-multi-bin-btn"
                >
                  <Plus className="w-4 h-4" />
                  Aggiungi Bin
                </button>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-[#FFE500] text-[#0A1628] rounded-xl font-bold uppercase tracking-wide text-xs shadow-lg shadow-[#FFE500]/20 hover:bg-[#FFE500]/90 transition-colors"
                  data-testid="save-multi-bins-btn"
                >
                  <Save className="w-4 h-4" />
                  Salva {multiBinsItems.length} Bins
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Filtri */}
      <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5" data-testid="filters-section">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-[#FFE500]" />
          <h3 className="text-sm font-bold text-white uppercase tracking-tight">Filtri</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Data Entrata</label>
            <input
              type="date"
              value={filterData}
              onChange={(e) => setFilterData(e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
              data-testid="filter-data"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Doccia/Fila</label>
            <select
              value={filterDoccia}
              onChange={(e) => setFilterDoccia(e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
              data-testid="filter-doccia"
            >
              <option value="all" className="bg-[#0A1628]">Tutte</option>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                <option key={num} value={num} className="bg-[#0A1628]">Doccia {num}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Ricerca</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cerca bin, lotto o fornitore..."
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
              data-testid="filter-search"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Raggruppa per</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
              data-testid="group-by-select"
            >
              <option value="none" className="bg-[#0A1628]">Nessuno</option>
              <option value="doccia" className="bg-[#0A1628]">Doccia</option>
              <option value="lotto" className="bg-[#0A1628]">Lotto</option>
            </select>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm text-white/50">
            Mostrando <span className="text-white font-medium">{filteredBins.length}</span> di <span className="text-white font-medium">{bins.length}</span> bins attivi
            {groupBy !== 'none' && <span className="ml-2 font-medium text-[#FFE500]">• Raggruppati per {groupBy}</span>}
          </span>
          {(filterData || filterDoccia !== 'all' || searchTerm || groupBy !== 'none') && (
            <button
              onClick={() => { setFilterData(''); setFilterDoccia('all'); setSearchTerm(''); setGroupBy('none'); }}
              className="text-sm text-red-400 hover:text-red-300 transition-colors"
              data-testid="clear-filters-btn"
            >
              Pulisci filtri
            </button>
          )}
        </div>
      </div>

      {/* Toggle Preview Docce */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowPreview(!showPreview)}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold uppercase tracking-wide text-xs transition-colors ${
            showPreview 
              ? 'bg-[#FFE500] text-[#0A1628]' 
              : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
          }`}
          data-testid="toggle-preview-btn"
        >
          <Droplets className="w-4 h-4" />
          {showPreview ? 'Nascondi Preview' : 'Mostra Preview Docce'}
        </button>
      </div>

      {/* Preview Posizione Bins per Doccia */}
      {showPreview && (
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5" data-testid="docce-preview-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#FFE500] p-2 rounded-xl">
              <Droplets className="w-5 h-5 text-[#0A1628]" />
            </div>
            <h3 className="text-lg font-bold text-white uppercase tracking-tight">Preview Posizione Bins</h3>
            <span className="text-xs text-white/40 ml-auto">Usa le frecce per riordinare • Pos. 1 = Fondo, Pos. 4 = Cima</span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            {Object.entries(getBinsByDoccia()).map(([docciaNum, docciaBins]) => {
              const hasBins = docciaBins.length > 0;
              
              return (
                <div
                  key={docciaNum}
                  className={`rounded-xl p-3 transition-all ${
                    hasBins 
                      ? 'bg-white/10 border-2 border-[#FFE500]/30' 
                      : 'bg-white/5 border border-white/10'
                  }`}
                  data-testid={`preview-doccia-${docciaNum}`}
                >
                  <div className="text-xs font-bold text-white text-center mb-2">
                    Doccia {docciaNum}
                    {hasBins && <span className="ml-1 text-[#FFE500]">({docciaBins.length}/4)</span>}
                  </div>
                  
                  {hasBins ? (
                    <div className="relative bg-gradient-to-b from-blue-900/30 to-blue-950/50 rounded-lg p-2 border border-blue-500/20">
                      <div className="flex flex-col gap-1">
                        {docciaBins
                          .sort((a, b) => (b.posizione || 1) - (a.posizione || 1))
                          .map(bin => (
                            <div
                              key={bin.bin_number}
                              className="bg-gradient-to-r from-emerald-600/80 to-emerald-500/80 rounded p-1.5 border border-emerald-400/30"
                            >
                              <div className="flex items-center justify-center gap-2">
                                <div className="text-center min-w-0">
                                  <div className="text-[10px] font-bold text-white truncate">{bin.bin_number}</div>
                                  <div className="text-[8px] text-white/70">{bin.kg_attuali}kg</div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  ) : (
                    <div className="h-24 flex items-center justify-center">
                      <span className="text-[10px] text-white/20">Vuota</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal Uscita Parziale */}
      {showUscitaModal && selectedBin && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0A1628] border border-white/20 rounded-2xl p-6 max-w-md w-full" data-testid="uscita-modal">
            <h3 className="text-lg font-bold text-white uppercase tracking-tight mb-4">
              Uscita - Bin {selectedBin.bin_number}
            </h3>
            <p className="text-sm text-white/50 mb-4">
              KG Disponibili: <span className="font-bold text-[#FFE500]">{selectedBin.kg_attuali} kg</span>
            </p>
            <form onSubmit={handleSubmitUscita} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1 uppercase">KG da far uscire</label>
                <input
                  type="number"
                  step="0.01"
                  max={selectedBin.kg_attuali}
                  value={uscitaData.kg_uscita}
                  onChange={(e) => setUscitaData({ ...uscitaData, kg_uscita: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                  required
                  data-testid="kg-uscita-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Data e Ora Uscita</label>
                <input
                  type="datetime-local"
                  value={uscitaData.data_ora_uscita}
                  onChange={(e) => setUscitaData({ ...uscitaData, data_ora_uscita: e.target.value })}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                  required
                  data-testid="data-uscita-input"
                />
              </div>
              <div className="border-t border-white/10 pt-4">
                <p className="text-sm font-medium text-white mb-2">Opzionale: Sposta Residuo</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-white/50 mb-1">Nuovo Numero Bin</label>
                    <input
                      type="text"
                      value={uscitaData.nuovo_bin_number}
                      onChange={(e) => setUscitaData({ ...uscitaData, nuovo_bin_number: e.target.value })}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                      placeholder="es. 19-R"
                      data-testid="nuovo-bin-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1">Nuova Doccia</label>
                    <select
                      value={uscitaData.nuova_doccia_number}
                      onChange={(e) => setUscitaData({ ...uscitaData, nuova_doccia_number: e.target.value })}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                      data-testid="nuova-doccia-select"
                    >
                      <option value="" className="bg-[#0A1628]">Mantieni doccia attuale</option>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                        <option key={num} value={num} className="bg-[#0A1628]">Doccia {num}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-xl font-bold uppercase tracking-wide text-xs hover:bg-emerald-600 transition-colors"
                  data-testid="conferma-uscita-btn"
                >
                  <LogOut className="w-4 h-4" />
                  Conferma Uscita
                </button>
                <button
                  type="button"
                  onClick={() => setShowUscitaModal(false)}
                  className="px-4 py-2 bg-white/10 text-white border border-white/20 rounded-xl font-bold uppercase tracking-wide text-xs hover:bg-white/20 transition-colors"
                  data-testid="annulla-uscita-btn"
                >
                  Annulla
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tabella Bins */}
      <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl overflow-hidden" data-testid="bins-table-container">
        <div className="overflow-x-auto">
          <table className="text-sm w-full border-collapse" data-testid="bins-table">
            <thead>
              <tr className="bg-[#FFE500]/10">
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-3 text-center">Doccia</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-3 text-center">Bin</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-3 text-center">Lotto</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-3 text-center">ID</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-3 text-center">Prodotto</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-3 text-center">Fornitore</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-3 text-center">KG Iniz.</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-3 text-center">KG Att.</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-3 text-center">Data Entrata</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-3 text-center w-32">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredBins.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center py-8 text-white/50" data-testid="no-bins-message">
                    Nessun bin attivo trovato.
                  </td>
                </tr>
              ) : (
                filteredBins.map((bin, index) => (
                  <tr key={bin.bin_number} className="border-b border-white/10 hover:bg-white/5 transition-colors" data-testid={`bin-row-${index}`}>
                    {editingBin === bin.bin_number ? (
                      <>
                        <td className="py-2 px-3 text-center">
                          <select
                            value={editForm.doccia_number}
                            onChange={(e) => setEditForm({...editForm, doccia_number: e.target.value})}
                            className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white"
                          >
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                              <option key={num} value={num} className="bg-[#0A1628]">{num}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <input
                            type="text"
                            value={editForm.bin_number}
                            onChange={(e) => setEditForm({...editForm, bin_number: e.target.value})}
                            className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white"
                          />
                        </td>
                        <td className="py-2 px-3 text-center">
                          <input
                            type="text"
                            value={editForm.lotto_number}
                            onChange={(e) => setEditForm({...editForm, lotto_number: e.target.value})}
                            className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white"
                          />
                        </td>
                        <td className="py-2 px-3 text-center">
                          <select
                            value={editForm.id_fornitore || ''}
                            onChange={(e) => setEditForm({...editForm, id_fornitore: e.target.value})}
                            className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white"
                          >
                            <option value="" className="bg-[#0A1628]">-</option>
                            {idFornitori.map(idf => (
                              <option key={idf.codice} value={idf.codice} className="bg-[#0A1628]">{idf.codice}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <select
                            value={editForm.prodotto || ''}
                            onChange={(e) => setEditForm({...editForm, prodotto: e.target.value})}
                            className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white"
                          >
                            <option value="" className="bg-[#0A1628]">-</option>
                            {prodotti.map(p => (
                              <option key={p.nome} value={p.nome} className="bg-[#0A1628]">{p.nome}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <select
                            value={editForm.fornitore}
                            onChange={(e) => setEditForm({...editForm, fornitore: e.target.value})}
                            className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white"
                          >
                            {fornitori.map(f => (
                              <option key={f.nome} value={f.nome} className="bg-[#0A1628]">{f.nome}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <input
                            type="number"
                            step="0.01"
                            value={editForm.kg_iniziali}
                            onChange={(e) => setEditForm({...editForm, kg_iniziali: e.target.value})}
                            className="w-16 px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white"
                          />
                        </td>
                        <td className="py-2 px-3 text-center">
                          <input
                            type="number"
                            step="0.01"
                            value={editForm.kg_attuali}
                            onChange={(e) => setEditForm({...editForm, kg_attuali: e.target.value})}
                            className="w-16 px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white"
                          />
                        </td>
                        <td className="py-2 px-3 text-center">
                          <input
                            type="datetime-local"
                            value={editForm.data_inizio}
                            onChange={(e) => setEditForm({...editForm, data_inizio: e.target.value})}
                            className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white"
                          />
                        </td>
                        <td className="py-2 px-3 text-center">
                          <div className="flex justify-center gap-1">
                            <button onClick={() => saveEdit(bin.bin_number)} className="p-1 text-emerald-400 hover:bg-emerald-500/20 rounded">
                              <Check className="w-4 h-4" />
                            </button>
                            <button onClick={cancelEdit} className="p-1 text-white/50 hover:bg-white/10 rounded">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-3 px-3 text-center font-bold text-white">{bin.doccia_number}</td>
                        <td className="py-3 px-3 text-center font-medium text-white">{bin.bin_number}</td>
                        <td className="py-3 px-3 text-center text-white/70">{bin.lotto_number}</td>
                        <td className="py-3 px-3 text-center text-cyan-400 font-bold">{bin.id_fornitore || '-'}</td>
                        <td className="py-3 px-3 text-center">
                          {bin.prodotto ? (
                            <span className="inline-block px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-lg font-medium">
                              {bin.prodotto}
                            </span>
                          ) : (
                            <span className="text-white/30">-</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-center text-white/70">{bin.fornitore}</td>
                        <td className="py-3 px-3 text-center font-bold text-white">{bin.kg_iniziali}</td>
                        <td className="py-3 px-3 text-center font-bold text-emerald-400">{bin.kg_attuali}</td>
                        <td className="py-3 px-3 text-center text-white/70">{format(new Date(bin.data_inizio), 'dd/MM/yy HH:mm')}</td>
                        <td className="py-3 px-3 text-center">
                          <div className="flex justify-center gap-1">
                            <button
                              onClick={() => startEdit(bin)}
                              className="p-1.5 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors"
                              title="Modifica"
                              data-testid={`edit-bin-${index}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleUscitaParziale(bin)}
                              className="p-1.5 text-amber-400 hover:bg-amber-500/20 rounded-lg transition-colors"
                              title="Uscita"
                              data-testid={`uscita-bin-${index}`}
                            >
                              <LogOut className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(bin.bin_number)}
                              className="p-1.5 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                              title="Elimina"
                              data-testid={`delete-bin-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Bins;
